import rehypeShikiFromHighlighter from "@shikijs/rehype/core";
import { highlighter } from "@/shared/shiki";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { useEffect, useMemo, useRef } from "react";
import ReactMarkdown from "react-markdown";
import rehypeAutolinkHeadings from "rehype-autolink-headings";
import rehypeSlug from "rehype-slug";
import remarkGfm from "remark-gfm";
import { Button, EmptyState, ErrorBoundary, Skeleton, StateMatrix } from "@/shared/ui";
import {
  CodeFileReader,
  PlainTextReader,
  ctMimeToLang,
  detectCodeLang,
  sniffCodeLangFromBody,
} from "./code-file-reader";
import { documentQuery, documentsQuery } from "./queries";
import { SmartLink } from "./smart-link";

export function DocumentReader() {
  const { name, _splat } = useParams({ from: "/_authed/catalogs/$name/$" });
  const slug = _splat ?? "";
  const q = useQuery(documentQuery(name, slug));
  const docList = useQuery(documentsQuery(name));

  const body = useMemo(
    () => (q.data ? q.data.lines.map((l) => l.text).join("\n") : ""),
    [q.data],
  );

  const knownSlugs = useMemo(
    () => new Set((docList.data ?? []).map((d) => d.slug)),
    [docList.data],
  );

  // Resolve renderer:
  //   1. revision.contentType (authoritative) → mime → shiki lang | 'plain' | 'markdown'
  //   2. slug extension
  //   3. content sniff (last-resort, for pre-contentType backends)
  const renderMode = useMemo<"markdown" | "plain" | { lang: string }>(() => {
    const ct = q.data?.revision.contentType;
    const fromCt = ctMimeToLang(ct);
    if (fromCt === "markdown") return "markdown";
    if (fromCt === "plain") return "plain";
    if (fromCt) return { lang: fromCt };
    const fromExt = detectCodeLang(slug);
    if (fromExt) return { lang: fromExt };
    const fromSniff = sniffCodeLangFromBody(body);
    if (fromSniff) return { lang: fromSniff };
    return "markdown";
  }, [q.data?.revision.contentType, slug, body]);

  return (
    <article className="max-w-[80ch] px-10 py-10 pr-8">
      <StateMatrix
        status={q.status}
        data={q.data}
        error={q.error}
        onRetry={() => q.refetch()}
        loading={<ReaderSkeleton />}
      >
        {(doc) => {
          const title =
            doc.revision.title ??
            doc.document.title ??
            prettifyLeaf(leafFromSlug(doc.document.slug));
          // Strip redundant leading H1 *before* the empty check so docs whose
          // body is just `# Title\n` (Mintlify api-reference/introduction
          // stub etc.) collapse to empty and render the empty state.
          const rawDescription: string | undefined =
            (typeof doc.revision.description === "string" && doc.revision.description) ||
            (typeof doc.document.description === "string" && doc.document.description) ||
            undefined;
          const renderableBody =
            renderMode === "markdown"
              ? stripRedundantLeadingHeadings(body, title, rawDescription)
              : body;
          const category: string | undefined =
            typeof doc.revision.category === "string" && doc.revision.category !== ""
              ? doc.revision.category
              : undefined;
          // Strip a leading `>` if the description was extracted verbatim
          // from a blockquote paragraph in the body.
          const description: string | undefined = rawDescription
            ? rawDescription.replace(/^>\s*/, "").trim() || undefined
            : undefined;
          const headerProps: Parameters<typeof ReaderHeader>[0] = {
            catalog: name,
            title,
            truncated: doc.truncated,
            bytesReturned: doc.bytesReturned,
            totalBytes: doc.totalBytes,
          };
          if (category !== undefined) headerProps.category = category;
          if (description !== undefined) headerProps.description = description;
          return (
          <>
            <Breadcrumb catalog={name} slug={doc.document.slug} />
            <ReaderHeader {...headerProps} />
            {renderableBody.trim().length === 0 ? (
              <EmptyState
                title="This document is empty"
                description="The source returned no body. Try refreshing the catalog from Admin."
              />
            ) : renderMode === "plain" ? (
              <PlainTextReader body={body} />
            ) : typeof renderMode === "object" ? (
              <CodeFileReader body={body} lang={renderMode.lang} />
            ) : (
              <ErrorBoundary
                key={slug}
                fallback={(err, reset) => (
                  <div className="flex flex-col gap-3 rounded-md border border-[var(--danger)]/40 bg-[var(--danger)]/5 p-4">
                    <div>
                      <p className="text-sm font-medium text-[var(--danger)]">
                        Could not render markdown
                      </p>
                      <p className="text-xs text-[var(--fg-muted)]">{err.message}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="secondary" onClick={reset}>
                        Try again
                      </Button>
                    </div>
                    <details className="mt-2">
                      <summary className="cursor-pointer text-xs text-[var(--fg-muted)]">
                        Show raw source
                      </summary>
                      <pre className="mt-2 max-h-[60vh] overflow-auto rounded-md border border-[var(--border)] bg-[var(--surface-2)] p-3 font-mono text-xs">
                        {body}
                      </pre>
                    </details>
                  </div>
                )}
              >
                <MarkdownBody
                  body={renderableBody}
                  catalog={name}
                  currentSlug={slug}
                  knownSlugs={knownSlugs}
                />
              </ErrorBoundary>
            )}
          </>
          );
        }}
      </StateMatrix>
    </article>
  );
}

function leafFromSlug(slug: string): string {
  const parts = slug.split("/").filter(Boolean);
  return parts[parts.length - 1] ?? slug;
}

/** Turn a kebab/snake slug into a human title: "agent-tools-deprecation" → "Agent tools deprecation". */
function prettifyLeaf(leaf: string): string {
  const spaced = leaf.replace(/[-_]+/g, " ").trim();
  if (!spaced) return leaf;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function ReaderHeader({
  catalog,
  category,
  title,
  description,
  truncated,
  bytesReturned,
  totalBytes,
}: {
  catalog: string;
  category?: string;
  title: string;
  description?: string;
  truncated: boolean;
  bytesReturned: number;
  totalBytes: number;
}) {
  const ref = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    // Focus the heading on mount / route change for screen-reader parity.
    ref.current?.focus();
  }, [title]);

  return (
    <header className="mb-6 border-b border-[var(--border)] pb-4">
      <h1
        ref={ref}
        tabIndex={-1}
        className="text-3xl font-semibold tracking-tight outline-none"
      >
        {title}
      </h1>
      {description && (
        <p className="mt-2 text-sm text-[var(--fg-muted)]">{description}</p>
      )}
      {truncated && (
        <p className="mt-3 rounded-md bg-[var(--surface-2)] px-3 py-2 text-xs text-[var(--fg-muted)]">
          Showing first {bytesReturned.toLocaleString()} of {totalBytes.toLocaleString()} bytes.
        </p>
      )}
    </header>
  );
}

function Breadcrumb({ catalog, slug }: { catalog: string; slug: string }) {
  const parts = slug.split("/").filter(Boolean);
  return (
    <nav aria-label="Breadcrumb" className="mb-3">
      <ol className="flex flex-wrap items-center gap-1 text-[11px] text-[var(--fg-muted)]">
        <li>
          <Link
            to="/catalogs/$name"
            params={{ name: catalog }}
            className="hover:text-[var(--fg)] hover:underline"
          >
            {catalog}
          </Link>
        </li>
        {parts.slice(0, -1).map((p, i) => (
          <li key={`${p}-${i}`} className="flex items-center gap-1">
            <Chevron />
            <span>{p}</span>
          </li>
        ))}
        {parts.length > 0 && (
          <li className="flex items-center gap-1">
            <Chevron />
            <span className="font-medium text-[var(--fg)]">{parts[parts.length - 1]}</span>
          </li>
        )}
      </ol>
    </nav>
  );
}

function Chevron() {
  return (
    <svg
      aria-hidden
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className="text-[var(--fg-muted)]/60"
    >
      <path d="m9 6 6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * Strip redundant leading H1s from the body so we don't render the title
 * twice on top of the ReaderHeader.
 *
 * Many docs sources (Mintlify llms-full.txt, Slate, MDX with auto-titles
 * etc.) emit `# Title` as the first heading of every document. The webapp
 * already renders that title in the ReaderHeader — keeping it in the body
 * shows it a second (and sometimes third) time. We also collapse runs of
 * identical adjacent H1s, which the lettermint llms-full.txt source has
 * (a `# Introduction` → `<Frame>...</Frame>` → `# Introduction` pattern
 * leaves two H1s after the strip-mdx cleaner).
 */
function stripRedundantLeadingHeadings(
  body: string,
  title: string,
  description: string | undefined,
): string {
  let s = body.replace(/^\s+/, "");
  const titleNorm = title.trim().toLowerCase();
  const descNorm = description?.replace(/^>\s*/, "").trim().toLowerCase();
  const stripHashes = (h: string) => h.replace(/^#+\s*/, "").trim().toLowerCase();
  const stripBlockquote = (h: string) =>
    h
      .split("\n")
      .map((l) => l.replace(/^>\s?/, ""))
      .join("\n")
      .trim()
      .toLowerCase();

  // Repeatedly strip leading H1s (matching title) and blockquotes (matching
  // description) from the top of the body. Many adapters derive description
  // from the first body paragraph (often a blockquote), so that paragraph
  // then also appears verbatim at the top of the body — redundant once the
  // ReaderHeader renders the description.
  for (let i = 0; i < 4; i++) {
    const before = s;
    // Leading H1 matching title.
    const h1 = s.match(/^#\s+([^\n]+)\n+/);
    if (h1 && stripHashes(h1[0]) === titleNorm) {
      s = s.slice(h1[0].length).replace(/^\s+/, "");
      continue;
    }
    // Leading blockquote (possibly multi-line) matching description.
    if (descNorm) {
      const bq = s.match(/^((?:>[^\n]*(?:\n|$))+)\n*/);
      if (bq?.[1] && stripBlockquote(bq[1]) === descNorm) {
        s = s.slice(bq[0].length).replace(/^\s+/, "");
        continue;
      }
    }
    if (s === before) break;
  }

  // Collapse runs of identical adjacent H1s anywhere in the body.
  s = s.replace(/(^|\n)(#\s+[^\n]+)\n+(?:\2\n+)+/g, "$1$2\n\n");
  return s;
}

function MarkdownBody({
  body,
  catalog,
  currentSlug,
  knownSlugs,
}: {
  body: string;
  catalog: string;
  currentSlug: string;
  knownSlugs: Set<string>;
}) {
  return (
    <div className="prose-rtfm">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[
          rehypeSlug,
          [
            rehypeAutolinkHeadings,
            {
              behavior: "append",
              properties: { className: ["heading-anchor"], ariaLabel: "Link to heading" },
              content: { type: "text", value: "#" },
            },
          ],
          [
            rehypeShikiFromHighlighter,
            highlighter,
            {
              themes: { light: "github-light", dark: "github-dark" },
              defaultColor: false,
              fallbackLanguage: "text",
            },
          ],
        ]}
        components={{
          a: ({ node, ...props }) => (
            <SmartLink
              catalog={catalog}
              currentSlug={currentSlug}
              knownSlugs={knownSlugs}
              {...props}
            />
          ),
        }}
      >
        {body}
      </ReactMarkdown>
    </div>
  );
}

function ReaderSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      <Skeleton className="h-8 w-2/3" />
      <Skeleton className="h-4 w-1/2" />
      <Skeleton className="mt-4 h-4 w-full" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-5/6" />
      <Skeleton className="h-4 w-3/4" />
    </div>
  );
}
